import { IBalanceForecastDay } from '@root/query'
import { formatDate } from '@utils'
import React from 'react'
import { useTranslation } from 'react-i18next'
import { FaRegClock } from 'react-icons/fa'
import styled from 'styled-components'
import { WHEN_STACKED } from '../../layout'
import { BalanceSparkline, BalanceSparklineSkeleton, ScrubPoint } from './BalanceSparkline'
import { NavyShimmer } from './CashAccounts'

interface Props {
  days: IBalanceForecastDay[]
  isSkeleton: boolean
  updatedOn?: string
  onScrub: (point: ScrubPoint | null) => void
}

export const CashHeroChart: React.FC<Props> = ({ days, isSkeleton, updatedOn, onScrub }) => {
  const [t] = useTranslation()
  const firstDay = days[0]
  const lastDay = days[days.length - 1]
  const todayIndex = Math.max(
    0,
    days.findIndex(day => day.current)
  )
  const todayRatio = days.length > 1 ? todayIndex / (days.length - 1) : 0

  return (
    <ChartBlock>
      {isSkeleton ? (
        <BalanceSparklineSkeleton />
      ) : (
        <BalanceSparkline days={days} formatDate={formatDate} onScrub={onScrub} />
      )}
      <AxisRow>
        {isSkeleton ? (
          <>
            <NavyShimmer width="3.5rem" height="0.5rem" borderRadius="0.25rem" />
            <NavyShimmer width="2.75rem" height="0.5rem" borderRadius="0.25rem" />
            <NavyShimmer width="3.5rem" height="0.5rem" borderRadius="0.25rem" />
          </>
        ) : (
          <>
            <AxisEnd>{firstDay && formatDate(firstDay.date)}</AxisEnd>
            <AxisToday style={{ left: `${todayRatio * 100}%` }}>
              {t('dashboard.cash.today')}
            </AxisToday>
            <AxisEnd>{lastDay && formatDate(lastDay.date)}</AxisEnd>
          </>
        )}
      </AxisRow>
      <LegendRow>
        {isSkeleton ? (
          <>
            <NavyShimmer width="7rem" height="0.5rem" borderRadius="0.25rem" />
            <Spacer />
            <NavyShimmer width="4rem" height="0.5rem" borderRadius="0.25rem" />
            <NavyShimmer width="4rem" height="0.5rem" borderRadius="0.25rem" />
          </>
        ) : (
          <>
            <LegendItem>
              <FaRegClock />
              {t('dashboard.cash.updatedOn', {
                date: updatedOn ? formatDate(updatedOn) : ''
              })}
            </LegendItem>
            <Spacer />
            <LegendItem>
              <SolidSwatch />
              {t('dashboard.cash.legendActual')}
            </LegendItem>
            <LegendItem>
              <DashedSwatch>
                <i />
                <i />
              </DashedSwatch>
              {t('dashboard.cash.legendForecast')}
            </LegendItem>
          </>
        )}
      </LegendRow>
    </ChartBlock>
  )
}

const Spacer = styled.div.attrs({ ['aria-hidden']: true })`
  flex: 1;
`

const ChartBlock = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: flex-end;
  gap: ${({ theme }) => theme.spacing.xxs}rem;
  margin: ${({ theme }) => theme.spacing.xxs}rem 0 0;
  flex: 1;
  min-height: 0;

  ${WHEN_STACKED} {
    flex: 0 0 auto;
  }
`

const AxisRow = styled.div`
  position: relative;
  display: flex;
  justify-content: space-between;
  gap: ${({ theme }) => theme.spacing.xs}rem;
  font-size: ${({ theme }) => theme.fontSize.px10}rem;
  color: ${({ theme }) => theme.dashboard.onNavyAxis};
`

const AxisEnd = styled.span`
  white-space: nowrap;
`

const AxisToday = styled.span`
  position: absolute;
  transform: translateX(-50%);
  font-weight: 600;
  white-space: nowrap;
  color: ${({ theme }) => theme.colors.neutralWhite};
`

const LegendRow = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: ${({ theme }) => theme.spacing.md}rem;
  padding-top: ${({ theme }) => theme.spacing.xxs}rem;
`

const LegendItem = styled.span`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.xs}rem;
  font-size: ${({ theme }) => theme.fontSize.px11}rem;
  color: ${({ theme }) => theme.dashboard.onNavyAxis};
  white-space: nowrap;

  svg {
    font-size: ${({ theme }) => theme.fontSize.xxs}rem;
  }
`

const SolidSwatch = styled.span`
  width: 0.875rem;
  height: 0.125rem;
  border-radius: 2px;
  background: ${({ theme }) => theme.luca.sky};
`

const DashedSwatch = styled.span`
  display: flex;
  gap: 2px;
  width: 0.875rem;

  i {
    flex: 1;
    height: 0.125rem;
    border-radius: 2px;
    background: ${({ theme }) => theme.colors.primary};
  }
`
